import React, { useEffect, useState } from 'react';
import { WifiOff } from 'lucide-react';
import { SIDEBAR_WIDTH } from './NavBar';
import { MOBILE_NAV_HEIGHT } from './MobileNavBar';
import useMediaQuery from '../utils/useMediaQuery';

const isOnline = () => (typeof navigator === 'undefined' || navigator.onLine !== false);

export function useOnlineStatus() {
  const [online, setOnline] = useState(isOnline);

  useEffect(() => {
    const update = () => setOnline(isOnline());
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    update();
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  return online;
}

/**
 * Small pill shown while the browser is offline. In the app it sits at the top of the
 * content column (below the mobile top bar, clear of the desktop sidebar) so it never
 * hides the sticky save bars at the bottom; on signed-out pages it sits at the bottom,
 * clear of the landing nav. Clicks pass through to whatever is underneath.
 */
export default function OfflineBanner({ signedIn = false }) {
  const online = useOnlineStatus();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const position = signedIn
    ? { left: isDesktop ? SIDEBAR_WIDTH : 0, top: (isDesktop ? 0 : MOBILE_NAV_HEIGHT) + 8 }
    : { left: 0, bottom: 'calc(0.75rem + env(safe-area-inset-bottom))' };

  return (
    <div
      aria-live="polite"
      data-testid="offline-banner"
      className="pointer-events-none fixed right-0 z-[80] flex justify-center px-4"
      style={position}
    >
      {!online && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-full border border-warn/30 bg-surface-raised px-3 py-1.5 text-xs text-fg shadow-elevated"
        >
          <WifiOff className="h-3.5 w-3.5 shrink-0 text-warn" aria-hidden="true" />
          <span>You're offline. Changes will sync when you reconnect.</span>
        </div>
      )}
    </div>
  );
}
